import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { vetrineContentService } from '../../../services/vetrineContentService';
import { format } from 'date-fns';

const PromotionsManager = ({ storeId }) => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    tipo: 'sconto_percentuale',
    valoreSconto: '',
    condizioni: '',
    dataInizio: '',
    dataFine: '',
    stato: 'programmata',
    coloreBadge: '#FF6B6B',
    inEvidenza: false
  });

  useEffect(() => {
    loadPromotions();
  }, [storeId]);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const data = await vetrineContentService?.getPromotionsByStore(storeId);
      setPromotions(data);
    } catch (error) {
      console.error('Error loading promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const promotionData = {
        ...formData,
        storeId,
        valoreSconto: formData?.valoreSconto ? parseFloat(formData?.valoreSconto) : null
      };

      if (editingPromotion) {
        await vetrineContentService?.updatePromotion(editingPromotion?.id, promotionData);
      } else {
        await vetrineContentService?.createPromotion(promotionData);
      }

      await loadPromotions();
      resetForm();
    } catch (error) {
      console.error('Error saving promotion:', error);
      alert('Errore nel salvare la promozione: ' + error?.message);
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      titolo: promotion?.titolo || '',
      descrizione: promotion?.descrizione || '',
      tipo: promotion?.tipo || 'sconto_percentuale',
      valoreSconto: promotion?.valoreSconto || '',
      condizioni: promotion?.condizioni || '',
      dataInizio: promotion?.dataInizio ? promotion?.dataInizio?.split('T')?.[0] : '',
      dataFine: promotion?.dataFine ? promotion?.dataFine?.split('T')?.[0] : '',
      stato: promotion?.stato || 'programmata',
      coloreBadge: promotion?.coloreBadge || '#FF6B6B',
      inEvidenza: promotion?.inEvidenza ?? false
    });
    setShowForm(true);
  };

  const handleDelete = async (promotionId) => {
    if (!confirm('Sei sicuro di voler eliminare questa promozione?')) return;
    
    try {
      await vetrineContentService?.deletePromotion(promotionId);
      await loadPromotions();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      alert('Errore nell\'eliminare la promozione: ' + error?.message);
    }
  };

  const resetForm = () => {
    setFormData({
      titolo: '',
      descrizione: '',
      tipo: 'sconto_percentuale',
      valoreSconto: '',
      condizioni: '',
      dataInizio: '',
      dataFine: '',
      stato: 'programmata',
      coloreBadge: '#FF6B6B',
      inEvidenza: false
    });
    setEditingPromotion(null);
    setShowForm(false);
  };

  const getStatoBadge = (stato) => {
    const badges = {
      programmata: { color: 'bg-blue-100 text-blue-700', label: 'Programmata' },
      attiva: { color: 'bg-green-100 text-green-700', label: 'Attiva' },
      scaduta: { color: 'bg-gray-100 text-gray-700', label: 'Scaduta' },
      archiviata: { color: 'bg-gray-100 text-gray-700', label: 'Archiviata' }
    };
    return badges?.[stato] || badges?.programmata;
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento promozioni...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Promozioni con Calendario ({promotions?.length})</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          <Icon name={showForm ? 'X' : 'Plus'} size={20} />
          {showForm ? 'Annulla' : 'Aggiungi Promozione'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-muted/30 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingPromotion ? 'Modifica Promozione' : 'Nuova Promozione'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Titolo Promozione *"
              value={formData?.titolo}
              onChange={(e) => setFormData({ ...formData, titolo: e?.target?.value })}
              required
              className="md:col-span-2"
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Descrizione *</label>
              <textarea
                value={formData?.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e?.target?.value })}
                rows={3}
                required
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tipo Promozione</label>
              <select
                value={formData?.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              >
                <option value="sconto_percentuale">Sconto Percentuale</option>
                <option value="sconto_fisso">Sconto Fisso</option>
                <option value="regalo">Regalo</option>
                <option value="cashback_extra">Cashback Extra</option>
                <option value="altro">Altro</option>
              </select>
            </div>
            <Input
              label="Valore Sconto"
              type="number"
              step="0.01"
              value={formData?.valoreSconto}
              onChange={(e) => setFormData({ ...formData, valoreSconto: e?.target?.value })}
            />
            <Input
              label="Data Inizio *"
              type="date"
              value={formData?.dataInizio}
              onChange={(e) => setFormData({ ...formData, dataInizio: e?.target?.value })}
              required
            />
            <Input
              label="Data Fine *"
              type="date"
              value={formData?.dataFine}
              onChange={(e) => setFormData({ ...formData, dataFine: e?.target?.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium mb-2">Stato</label>
              <select
                value={formData?.stato}
                onChange={(e) => setFormData({ ...formData, stato: e?.target?.value })}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              >
                <option value="programmata">Programmata</option>
                <option value="attiva">Attiva</option>
                <option value="scaduta">Scaduta</option>
                <option value="archiviata">Archiviata</option>
              </select>
            </div>
            <Input
              label="Colore Badge"
              type="color"
              value={formData?.coloreBadge}
              onChange={(e) => setFormData({ ...formData, coloreBadge: e?.target?.value })}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Condizioni</label>
              <textarea
                value={formData?.condizioni}
                onChange={(e) => setFormData({ ...formData, condizioni: e?.target?.value })}
                rows={2}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.inEvidenza}
                  onChange={(e) => setFormData({ ...formData, inEvidenza: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">In Evidenza</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button type="submit">
              <Icon name="Save" size={20} />
              {editingPromotion ? 'Aggiorna' : 'Salva'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Annulla
            </Button>
          </div>
        </form>
      )}

      {promotions?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Tag" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessuna promozione presente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {promotions?.map((promo) => {
            const statoBadge = getStatoBadge(promo?.stato);
            return (
              <div key={promo?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{promo?.titolo}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${statoBadge?.color}`}>
                        {statoBadge?.label}
                      </span>
                      {promo?.inEvidenza && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Evidenza</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{promo?.descrizione}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={16} />
                        {promo?.dataInizio && format(new Date(promo?.dataInizio), 'dd/MM/yyyy')} - {promo?.dataFine && format(new Date(promo?.dataFine), 'dd/MM/yyyy')}
                      </span>
                      {promo?.valoreSconto && (
                        <span className="font-semibold text-primary">
                          {promo?.tipo === 'sconto_percentuale' ? `${promo?.valoreSconto}%` : `€${promo?.valoreSconto}`}
                        </span>
                      )}
                      {promo?.visualizzazioni > 0 && (
                        <span className="text-muted-foreground">
                          👁 {promo?.visualizzazioni} visualizzazioni
                        </span>
                      )}
                    </div>
                    {promo?.condizioni && (
                      <p className="text-xs text-muted-foreground mt-2">📝 {promo?.condizioni}</p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(promo)}
                      className="p-2 hover:bg-muted rounded-lg transition-smooth"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(promo?.id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-smooth"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromotionsManager;